import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import Snackbar from '@mui/material/Snackbar';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { styled } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';

import NorowsData from '../../../pages/General/NorowsData';
import Notification from '../../../pages/General/Notification';
import { fetchTagData, storeTagData, updateTagData, destoryTagData, resetTagState } from './tagSettingsSlice';

// Global styling matrix configuration mappings for consistent corporate modal containers
const enterpriseModalContainerStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '92%', sm: 500 },
    outline: 'none',
    border: 'none',
    boxSizing: 'border-box'
};

const enterpriseDeleteModalStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 420 },
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    border: '1px solid #E2E8F0',
    p: 4,
    outline: 'none',
    overflow: 'hidden'
};

const enterpriseFormInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE IDENTITY ACCENT BRAND BLUE ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const TagSettingsPage = () => {
    const dispatch = useDispatch();
    const tags = useSelector(state => state.tags);
    const profile = useSelector(state => state.auth.profile);
    const [selectedItem, setSelectedItem] = useState({});
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteid, setDeleteId] = React.useState("");
    const [deleteModalopen, setDeleteModalopen] = React.useState(false);
    const [submitClick, setSubmitClick] = React.useState(false);
    const initialValues = {
        tag_name: '',
    }
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});

    const validator = (fieldValues) => {
        let temp = { ...errors }
        if ('tag_name' in fieldValues)
            temp.tag_name = fieldValues.tag_name ? "" : "This field is required."

        setErrors({
            ...temp
        })

        if (fieldValues === values)
            return Object.values(temp).every(x => x == "")
    }

    const onInputChange = e => {
        const { name, value } = e.target
        setValues({
            ...values,
            [name]: value
        })
        validator({ [name]: value })
    }
    
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        bgcolor: 'background.paper'
    };
    
    const deleteModalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };
    
    const handleClick = (id) => {
        setDeleteId(id)
        setDeleteModalopen(true);
    };
    
    const deleteModalClose = () => {
        setDeleteModalopen(false);
    };
    
    const deletetag = (id) => {
        dispatch(destoryTagData(profile && profile.id,id))
        setDeleteModalopen(false);
        onModalClose()
    }
    
    const onModalClose = () => {
        setModalVisible(false)
        setSelectedItem({})
        setErrors({})
    }
    
    const handleSubmit = e => {
        e.preventDefault()
        const isValid = validator(values)
        if (isValid) {
            if (selectedItem && Object.keys(selectedItem).length !== 0) {
                dispatch(updateTagData(profile && profile.id,selectedItem._id, values))
                setSubmitClick(true)
            } else {
                dispatch(storeTagData(profile && profile.id,values))
                setSubmitClick(true)
            }
        }
    }
    
    const columns = [
        {
          field: 'tag_name',
          headerName: 'Name',
          flex:1,
        },
        {
          field: 'action',
          headerName: 'Action',
          flex:1,
          renderCell: (params) => {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Tooltip title="Edit Tag" placement="top">
                        <IconButton
                            onClick={() => {
                                setSelectedItem(params.row);
                                setModalVisible(true)
                            }}
                            sx={{ 
                                color: '#64748B',
                                padding: '6px',
                                borderRadius: '8px',
                                border: '1px solid #E2E8F0',
                                backgroundColor: '#FFFFFF',
                                '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1', color: '#1E293B' }
                            }}
                        >
                            <EditIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Tag" placement="top">
                        <IconButton 
                            onClick={(e) => {
                                handleClick(params.row._id)
                            }}
                            sx={{ 
                                color: '#EF4444',
                                padding: '6px',
                                borderRadius: '8px',
                                border: '1px solid #FEE2E2',
                                backgroundColor: '#FFFFFF',
                                '&:hover': { backgroundColor: '#FEF2F2', borderColor: '#FCA5A5' }
                            }}
                        >
                            <DeleteOutlineOutlinedIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
        },
      ];

    useEffect(() => {
        if (selectedItem) {
            let tempValues = {
                tag_name: selectedItem.tag_name,
            };
            setValues(tempValues)
        }
    }, [selectedItem]);

    useEffect(() => {
        dispatch(fetchTagData());
    }, [dispatch]);

    useEffect(() => {
       if(tags && tags.loading==false && tags.notification && tags.notification.messageContent  && tags.notification.messageContent[0] !==''  && tags.notification.messageType!=='error' && submitClick==true){
            setSubmitClick(false)
            onModalClose()
       }
    }, [tags]);

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box',p:2.5 }}>
             {(tags && tags.notification && tags.notification.messageContent!='') &&
            <Notification
                is_open={tags.notification.show}
                messageType={tags.notification.messageType}
                messages={tags.notification.messageContent}
                handleClose={() => dispatch(resetTagState())}
            />}
            
            {/* Modal 1: Add or Update Tag Modal Window Pipeline */}
            <Modal
                open={modalVisible}
                onClose={onModalClose}
                aria-labelledby="tag-action-modal-title"
                aria-describedby="tag-action-modal-description"
                closeAfterTransition
            >
                <Box sx={enterpriseModalContainerStyles}>
                    <Card 
                        className="sc--cardhead--details"
                        sx={{ 
                            backgroundColor: '#FFFFFF',
                            borderRadius: '16px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            border: '1px solid #E2E8F0',
                            overflow: 'hidden'
                        }}
                    >
                        <CardHeader 
                            title={
                                <Typography id="tag-action-modal-title" variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px' }}>
                                    {selectedItem && Object.keys(selectedItem).length !== 0 ? 'Update Tag' : 'Add Tag'}
                                </Typography>
                            }
                            action={
                                <IconButton 
                                    onClick={onModalClose}
                                    sx={{ 
                                        color: '#64748B',
                                        '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B' }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: '20px' }} />
                                </IconButton>
                            }
                            sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                        />
                        <Divider sx={{ borderColor: '#E2E8F0' }} />
                        <CardContent sx={{ p: 3 }}>
                            <Box className="sc--form--center" sx={{ width: '100%', mb: 1, minHeight: '44px' }}>
                                <TextField
                                    label="Tag Name"
                                    name="tag_name"
                                    size="small"
                                    defaultValue={values && values.tag_name}
                                    onChange={onInputChange}
                                    error={!!errors.tag_name}
                                    helperText={errors.tag_name}
                                    variant="outlined"
                                    sx={enterpriseFormInputStyles}
                                />
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mt: 4 }}>
                                <Button 
                                    variant="outlined" 
                                    onClick={onModalClose}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 3,
                                        borderColor: '#E2E8F0',
                                        color: '#475569',
                                        '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC', color: '#1E293B' }
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    variant="contained" 
                                    onClick={handleSubmit}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 4,
                                        boxShadow: 'none',
                                        backgroundColor: '#1976d2',
                                        color: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                    }}
                                >
                                    Submit
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Modal >

            {/* Modal 2: Irreversible Entity Wipe Safe Intercept Dialog */}
             <Modal
                open={deleteModalopen}
                onClose={deleteModalClose}
                aria-labelledby="delete-tag-modal-title"
                aria-describedby="delete-tag-modal-description"
                closeAfterTransition
            >
                <Box sx={enterpriseDeleteModalStyles}>
                    <Typography id="delete-tag-modal-title" variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', mb: 1.5, textAlign: 'center', fontSize: '16px' }}>
                        Delete Tag
                    </Typography>
                    <Typography id="delete-tag-modal-description" variant="body2" sx={{ color: '#475569', fontWeight: 500, lineHeight: 1.6, mb: 4, textAlign: 'center' }}>
                        Are you sure want to delete this tag?<br />This deployment metadata profile lifecycle change is irreversible.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, width: '100%' }}>
                        <Button 
                            variant="outlined" 
                            onClick={deleteModalClose}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '10px',
                                fontWeight: 700,
                                fontSize: '13.5px',
                                height: '38px',
                                px: 3.5,
                                borderColor: '#E2E8F0',
                                color: '#475569',
                                '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC', color: '#1E293B' }
                            }}
                        >
                            No, Keep
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={(event) => deletetag(deleteid)}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '10px',
                                fontWeight: 700,
                                fontSize: '13.5px',
                                height: '38px',
                                px: 4.5,
                                boxShadow: 'none',
                                backgroundColor: '#EF4444',
                                color: '#FFFFFF',
                                '&:hover': { backgroundColor: '#DC2626', boxShadow: 'none' }
                            }}
                        >
                            Yes, Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Core Data Presentation Workspace Panel Surface */}
            <Card 
                className="scmaincard"
                sx={{ 
                    width: '100%', 
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                    overflow: 'hidden'
                }}
            >
                <Box 
                    className="sccardhead"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2.5,
                        borderBottom: '1px solid #E2E8F0',
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Typography 
                        variant="h5" 
                        component="h1" 
                        sx={{ 
                            fontWeight: 800, 
                            color: '#0F172A',
                            letterSpacing: '-0.5px',
                            margin: 0
                        }}
                    >
                        Tag Settings
                    </Typography>
                    
                    <Button 
                        variant="contained" 
                        onClick={() => { setModalVisible(true) }}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontWeight: 700,
                            fontSize: '13.5px',
                            height: '38px',
                            px: 3,
                            boxShadow: 'none',
                            backgroundColor: '#1976d2',
                            color: '#FFFFFF',
                            '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                        }}
                    >
                        Add Tag
                    </Button>
                </Box>
                <Divider />
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ width: '100%', backgroundColor: '#FFFFFF' }}>
                        <DataGrid
                            sx={{
                                border: 'none',
                                backgroundColor: '#FFFFFF',
                                overflow: 'hidden',
                                '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                                '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' } },
                                '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } },
                                '& .MuiTablePagination-root': { borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }
                            }}
                            getRowId={(row) => row._id}
                            disableRowSelectionOnClick={true}
                            rows={tags && tags.data}
                            columns={columns}
                            autoHeight={true}
                            slots={{
                                loadingOverlay: LinearProgress,
                                noRowsOverlay: NorowsData,
                            }}
                            loading={tags && tags.loading}
                        />
                    </Box>
                </CardContent>
            </Card >
        </Box>
    )
}

export default TagSettingsPage;